import { BaseCommand } from './ICommand';
import type { IEventBus } from '../events/IEventBus';
import type { CreateRootCommentPayload } from '../../domains/comments/models/Comment';

interface CreateRootCommentCommandProps {
  payload: CreateRootCommentPayload;
  createComment: (input: CreateRootCommentPayload) => Promise<void>;
  eventBus: IEventBus;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

export class CreateRootCommentCommand extends BaseCommand {
  private readonly payload: CreateRootCommentPayload;
  private readonly createComment: (input: CreateRootCommentPayload) => Promise<void>;
  private readonly eventBus: IEventBus;
  private readonly onSuccess?: () => void;
  private readonly onError?: (error: unknown) => void;
  private hasExecuted = false;

  constructor({ payload, createComment, eventBus, onSuccess, onError }: CreateRootCommentCommandProps) {
    // Comments are collaboration annotations persisted by the comments domain,
    // not authored organization facts. Keep them out of canvas history and the
    // live Atlas draft/apply guard.
    super('Create Comment', 'Create a new root comment', {
      skipHistory: true,
      executionScope: 'annotation',
    });
    this.payload = payload;
    this.createComment = createComment;
    this.eventBus = eventBus;
    this.onSuccess = onSuccess;
    this.onError = onError;
  }

  async execute(): Promise<void> {
    if (this.hasExecuted) {
      return;
    }

    try {
      await this.createComment(this.payload);
      this.onSuccess?.();
      this.eventBus.emit('comment:placement:completed');
      this.hasExecuted = true;
    } catch (error) {
      this.onError?.(error);
      throw error;
    }
  }

  async undo(): Promise<void> {
    // Comments are not undoable in v1.
  }

  async redo(): Promise<void> {
    // Re-running the mutation would duplicate the comment; no-op for now.
  }
}
